import React, { useCallback, useMemo } from 'react';
import { PropertySliderSection } from '@ui/PropertySliderSection';
import { useVersionExpressionVariablesRecord } from '@client/hooks/use-element-expression-variables.hooks';
import { getProject, useUpdateProject } from '@client/store/project';
import { updateElements } from '@shared/helpers/project.helpers';
import { ElementPropertyName } from '@shared/models/element-property.model';
import { IElement, OneOfPropertyElements } from '@shared/models/project.model';
import {
  getBulkPropertyValuesFromPropertyElements,
  useElementPropertyCommitHandler,
} from '@features/products/ProductBulkEditForm/product-bulk-edit-form.helpers';
import { applyElementCategoryProperties } from '@shared/helpers/element-category.helpers';
import {
  getElementProperty,
  isElementExpressionProperty,
} from '@shared/helpers/element-property.helpers';
import { getVariablesById } from '@shared/helpers/expression-variables.helpers';
import { toContentCoverageFactory } from '@shared/templates/categories/common-element-category-properties.helpers';
import { useAddProperty } from '@client/hooks/use-element-properties.hooks';
import { isBuildingVersionElement } from '@shared/helpers/recursive-element.helpers';
import { contentCoverageBoundErrorMessage } from './content-coverage-bound-error.helpers';

type ContentCoverageSectionProps = {
  element: OneOfPropertyElements;
  /** Align label row with property lists inside accordion panels (e.g. building version). */
  embedded?: boolean;
};

export const ContentCoverageSection: React.FC<ContentCoverageSectionProps> = ({
  element,
  embedded = false,
}) => {
  const variablesRecord = useVersionExpressionVariablesRecord();
  const updateProject = useUpdateProject();
  const addProperty = useAddProperty(element);

  const data = useMemo(
    () =>
      getBulkPropertyValuesFromPropertyElements({
        elements: [element],
        propertyName: ElementPropertyName.ContentCoverage,
        variablesRecord,
      }),
    [element, variablesRecord],
  );

  const boundErrorMessage = useMemo(() => {
    const property = getElementProperty(
      element,
      ElementPropertyName.ContentCoverage,
    );
    if (!isElementExpressionProperty(property)) return undefined;
    return contentCoverageBoundErrorMessage(
      property.count ?? property.fallbackCount,
      getVariablesById(variablesRecord, element.id),
    );
  }, [element, variablesRecord]);

  const onCommit = useElementPropertyCommitHandler(
    data.fromItems,
    ElementPropertyName.ContentCoverage,
    variablesRecord,
  );

  // Add content coverage when opening the slider if the row does not exist yet
  const enrichElementWithContentCoverageProperty = useCallback(() => {
    if (getElementProperty(element, ElementPropertyName.ContentCoverage)) {
      return;
    }

    if (isBuildingVersionElement(element)) {
      // Fire-and-forget: UI should not wait for project-change pipeline
      void addProperty(toContentCoverageFactory({ optional: true }));
      return;
    }

    const enriched = applyElementCategoryProperties(element as IElement);
    if (enriched !== element) {
      const updatedProject = updateElements(getProject(), enriched);
      // TODO(replay): express as ProjectChange so a 422 race can be replayed.
      updateProject({ kind: 'snapshot', project: updatedProject });
    }
  }, [addProperty, element, updateProject]);

  return (
    <PropertySliderSection
      label="Content coverage"
      data={data}
      inactiveValue={1}
      min={0.01}
      errorMessage={boundErrorMessage}
      resetKey={element.id}
      onCommit={onCommit}
      onShowForm={enrichElementWithContentCoverageProperty}
      embedded={embedded}
    />
  );
};
